<template>
  <div class="statistics-section">
    <div class="container-fluid px-0">
      <div class="statistics-grid">
        <div 
          v-for="(stat, index) in statistics" 
          :key="index"
          class="stat-card"
        >
          <div class="stat-icon-wrapper">
            <component :is="stat.icon" class="stat-icon" />
          </div>
          <div class="stat-number" :data-target="stat.value">{{ stat.value }}</div>
          <div class="stat-label">{{ stat.label }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { 
  ClockIcon, 
  UserGroupIcon, 
  CheckCircleIcon,
  StarIcon 
} from '@heroicons/vue/outline'

export default {
  name: 'Statistics',
  components: {
    ClockIcon,
    UserGroupIcon,
    CheckCircleIcon,
    StarIcon
  },
  props: {
    type: {
      type: String,
      default: 'auto', // 'auto' or 'digging'
      validator: (value) => ['auto', 'digging'].includes(value)
    }
  },
  computed: {
    companyAge() {
      // Įmonės įkūrimo data: 2018-11-06 (7 metai 2 mėnesiai 8 dienos nuo 2026-01-14)
      const foundedDate = new Date(2018, 10, 6); // Month is 0-indexed, so 10 = November
      const today = new Date();
      
      let years = today.getFullYear() - foundedDate.getFullYear();
      let months = today.getMonth() - foundedDate.getMonth();
      let days = today.getDate() - foundedDate.getDate();
      
      // Adjust for negative days
      if (days < 0) {
        months--;
        const lastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
        days += lastMonth.getDate();
      }
      
      // Adjust for negative months
      if (months < 0) {
        years--;
        months += 12;
      }
      
      return { years, months, days };
    },
    statistics() {
      const age = this.companyAge;
      const ageDisplay = age.years > 0 ? `${age.years}+` : `${age.months}+`;
      
      if (this.type === 'auto') {
        return [
          {
            icon: ClockIcon,
            value: ageDisplay,
            label: 'Metų patirtis'
          },
          {
            icon: UserGroupIcon,
            value: '700+',
            label: 'Patenkintų klientų'
          },
          {
            icon: CheckCircleIcon,
            value: '3500+',
            label: 'Atliktų remontų'
          },
          {
            icon: StarIcon,
            value: '9.7',
            label: 'Įvertinimas'
          }
        ]
      } else {
        return [
          {
            icon: ClockIcon,
            value: ageDisplay,
            label: 'Metų patirtis'
          },
          {
            icon: UserGroupIcon,
            value: '300+',
            label: 'Patenkintų klientų'
          },
          {
            icon: CheckCircleIcon,
            value: '1200+',
            label: 'Atliktų projektų'
          },
          {
            icon: StarIcon,
            value: '9.7',
            label: 'Įvertinimas'
          }
        ]
      }
    }
  },
  mounted() {
    this.animateNumbers()
  },
  methods: {
    animateNumbers() {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const target = entry.target
            const finalValue = target.getAttribute('data-target')
            this.countUp(target, finalValue)
            observer.unobserve(target)
          }
        })
      }, { threshold: 0.5 })

      this.$nextTick(() => {
        const numbers = document.querySelectorAll('.stat-number')
        numbers.forEach(num => observer.observe(num))
      })
    },
    countUp(element, finalValue) {
      // Skip animation for decimal values (like 9.7)
      if (finalValue.includes('.')) {
        element.textContent = finalValue
        return
      }
      
      const isPercentage = finalValue.includes('%')
      const isPlus = finalValue.includes('+')
      const numericValue = parseInt(finalValue.replace(/[^0-9]/g, ''))
      const duration = 2000
      const steps = 60
      const increment = numericValue / steps
      let current = 0
      const timer = setInterval(() => {
        current += increment
        if (current >= numericValue) {
          current = numericValue
          clearInterval(timer)
        }
        const displayValue = Math.floor(current)
        element.textContent = displayValue + (isPlus ? '+' : '') + (isPercentage ? '%' : '')
      }, duration / steps)
    }
  }
}
</script>

<style scoped>
.statistics-section {
  background: linear-gradient(135deg, #dc3545 0%, #ff6b6b 100%);
  padding: 80px 20px;
  position: relative;
  overflow: hidden;
}

.statistics-section::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: url('data:image/svg+xml,<svg width="100" height="100" xmlns="http://www.w3.org/2000/svg"><defs><pattern id="grid" width="100" height="100" patternUnits="userSpaceOnUse"><path d="M 100 0 L 0 0 0 100" fill="none" stroke="rgba(255,255,255,0.05)" stroke-width="1"/></pattern></defs><rect width="100%" height="100%" fill="url(%23grid)"/></svg>');
  opacity: 0.3;
}

.statistics-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 40px;
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
}

.stat-card {
  text-align: center;
  color: white;
}

.stat-icon-wrapper {
  width: 80px;
  height: 80px;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  transition: all 0.3s ease;
}

.stat-card:hover .stat-icon-wrapper {
  transform: scale(1.1) rotate(5deg);
  background: rgba(255, 255, 255, 0.3);
}

.stat-icon {
  width: 40px;
  height: 40px;
  color: white;
}

.stat-number {
  font-family: 'Montserrat', sans-serif;
  font-size: clamp(2.5rem, 5vw, 4rem);
  font-weight: 800;
  color: white;
  margin-bottom: 12px;
  text-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
  line-height: 1;
}

.stat-label {
  font-family: 'Open Sans', sans-serif;
  font-size: 1.1rem;
  color: rgba(255, 255, 255, 0.95);
  font-weight: 600;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
}

@media (max-width: 768px) {
  .statistics-section {
    padding: 60px 15px;
  }
  
  .statistics-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 30px;
  }
  
  .stat-icon-wrapper {
    width: 70px;
    height: 70px;
  }
  
  .stat-icon {
    width: 35px;
    height: 35px;
  }
}
</style>

